<template>
  <div class="main">
    <div class="d-flex justify-content-center" v-if="loadingSpinner">
      <b-spinner
        variant="success"
        label="Spinning"
        style="width: 3rem; height: 3rem"
      ></b-spinner>
    </div>

    <div v-if="!loadingSpinner">
      <div class="d-flex justify-content-center mb-1">
        <b-spinner
          variant="success"
          label="Spinning"
          block
          v-if="loading"
          style="width: 3rem; height: 3rem"
        ></b-spinner>
      </div>

      <b-row>
        <b-col cols="12">
          <div class="d-flex justify-content-between align-items-center">
            <!-- Calendar Checkbox -->
            <b-form-checkbox
              v-model="displayCalendar"
              name="check-button"
              switch
              inline
            >
              Calendar
            </b-form-checkbox>

            <!-- Add Service Order Button -->
            <b-button variant="primary" to="/serviceorder/add" class="my-1">
              Add Service Order
            </b-button>
          </div>
        </b-col>

        <div class="card">
          <div class="card-body">
            <div
              class="color-legend d-flex justify-content-start overflow-auto"
              style="white-space: nowrap"
            >
              <h5 class="mr-3">Status Legend</h5>
              <div
                v-for="status in serviceOrderStatus"
                :key="status.status"
                class="d-flex align-items-center mr-3"
              >
                <!-- Color box representing the status -->
                <div
                  :style="{
                    backgroundColor: status.color,
                    width: '20px',
                    height: '20px',
                    marginRight: '5px',
                    border: '1px solid #000',
                  }"
                ></div>
                <!-- Status text -->
                <span>{{ status.status.replace("_", " ") }}</span>
              </div>
            </div>
          </div>
        </div>
      </b-row>

      <!-- Calendar -->
      <div v-if="displayCalendar" class="mb-3">
        <b-row class="align-items-center">
          <!-- Customer Filter -->
          <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
            <b-form-group label="Filter by Customer" class="mb-0">
              <b-form-input
                v-model="calendarFilters.customer"
                placeholder="Enter customer name"
                @input="applyCalendarFilters"
              />
            </b-form-group>
          </b-col>

          <!-- Assignee Filter -->
          <b-col cols="12" sm="4" class="mb-2 mb-sm-0">
            <b-form-group label="Filter by Assignee" class="mb-0">
              <b-form-input
                v-model="calendarFilters.assignee"
                placeholder="Enter assignee name"
                @input="applyCalendarFilters"
              />
            </b-form-group>
          </b-col>

          <!-- Reset Button -->
          <b-col
            cols="12"
            sm="2"
            class="d-flex justify-content-sm-start justify-content-center"
          >
            <b-button variant="outline-secondary" @click="resetCalendarFilters">
              Reset Filters
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div v-if="displayCalendar">
        <Calendar
          :key="calendarKey"
          :CalendarDetails="CalendarDetails"
          v-if="!loading"
        />
      </div>

      <!-- Table -->
      <div v-else>
        <b-row class="justify-content-between">
          <b-col cols=" 12" md="2" sm="2" class="my-1">
            <b-form-group class="mb-0">
              <label class="d-inline-block text-sm-left mr-50">Per page</label>
              <b-form-select
                id="perPageSelect"
                v-model="perPage"
                size="sm"
                :options="pageOptions"
              />
            </b-form-group>
          </b-col>
          <b-col cols=" 12" md="2" sm="2" class="text-right my-2">
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="clearFilters"
              class="ml-2"
            >
              Clear Filters
            </b-button>
          </b-col>
        </b-row>
        <b-table
          striped
          hover
          responsive
          :per-page="perPage"
          :current-page="currentPage"
          :items="filteredItems"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          @filtered="onFiltered"
        >
          <!-- Title Column Header with Filter -->
          <template #head(title)="scope">
            <div class="column-header">
              <strong @click="toggleSort('title')">Service Order</strong>
              <b-input-group size="sm" class="mt-1">
                <b-form-input
                  v-model="filters.title"
                  placeholder="Filter by Template Name"
                  @input="applyFilters"
                />
              </b-input-group>
            </div>
          </template>

          <template #head(Assignee)="scope">
            <div class="column-header">
              <strong @click="toggleSort('title')">Assignee</strong>
              <b-input-group size="sm" class="mt-1">
                <b-form-input
                  v-model="filters.assignee"
                  placeholder="Filter by assignee"
                  @input="applyFilters"
                />
              </b-input-group>
            </div>
          </template>

          <template #head(Customer)="scope">
            <div class="column-header">
              <strong @click="toggleSort('title')">Customer</strong>
              <b-input-group size="sm" class="mt-1">
                <b-form-input
                  v-model="filters.customer"
                  placeholder="Filter by customer"
                  @input="applyFilters"
                />
              </b-input-group>
            </div>
          </template>

          <!-- Date Column Header with Date Picker -->
          <template #head(date)="scope">
            <div class="column-header">
              <strong @click="toggleSort('date')">Appointment Date</strong>
              <date-picker
                ref="datePicker"
                v-model="filters.date"
                :range="true"
                format="YYYY-MM-DD"
                placeholder="Select Date or Range"
                @change="applyFilters"
                @clear.prevent="preventClear"
                :clearable="false"
                :input-attr="{ readonly: true }"
                @focus="openCalendar"
                class="mt-1 w-100"
              />
            </div>
          </template>

          <!-- Status Column Header with Filter -->
          <template #head(status)="scope">
            <div class="column-header">
              <strong @click="toggleSort('status')">Status</strong>
              <b-form-select
                v-model="filters.status"
                @change="applyFilters"
                size="sm"
                class="mt-1"
              >
                <!-- Placeholder option -->
                <option :value="null" disabled>Select Status</option>
                <!-- Loop through the rest of the status options -->
                <option
                  v-for="status in statusOptions"
                  :key="status.value"
                  :value="status.value"
                >
                  {{ status.text }}
                </option>
              </b-form-select>
            </div>
          </template>

          <!-- Virtual Column for Row Index -->
          <template #cell(index)="data">
            {{ data.index + 1 }}
          </template>

          <!-- Status Column with Styling -->
          <template #cell(status)="data">
            <span
              class="status-badge"
              :style="{
                backgroundColor: getServiceOrderStatus(data.value)
                  .backgroundColor,
              }"
            >
              {{ data.value }}
            </span>
          </template>

          <template #cell(Assignee)="data">
            {{ data.item.assignee }}
          </template>
          <template #cell(Customer)="data">
            {{ data.item.name }}
          </template>

          <!-- Actions Column -->
          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item
                v-if="
                  data.item.status !== 'SR_COMPLETED' &&
                  data.item.status !== 'SR_INVOICED'
                "
                @click="editServiceOrder(data.item)"
              >
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>Edit</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="
                  data.item.status === 'SR_COMPLETED' ||
                  data.item.status === 'SR_INVOICED'
                "
                :to="{ path: `/serviceorder/view/${data.item.referenceNo}` }"
              >
                <feather-icon icon="EyeIcon" class="mr-50" />
                <span>View</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="data.item.status === 'SR_COMPLETED'"
                :to="{ path: `/invoice/add/${data.item.referenceNo}` }"
              >
                <feather-icon icon="ShoppingCartIcon" class="mr-50" />
                <span>Invoice</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="data.item.status === 'SR_INVOICED'"
                :to="{ path: `/invoice/view/${data.item.salesReference}` }"
              >
                <feather-icon icon="EyeIcon" class="mr-50" />
                <span>Invoice</span>
              </b-dropdown-item>
              <b-dropdown-item @click="addServiceOrderTimesheet(data.item)">
                <feather-icon icon="ClockIcon" class="mr-50" />
                <span>Timesheet</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template #head(actions)> </template>
        </b-table>
        <b-row>
          <b-col cols="12">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              size="sm"
              class="my-0"
            />
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
import Calendar from "../../../component/Calendar.vue";
import DatePicker from "vue2-datepicker"; // Import the date picker
import "vue2-datepicker/index.css"; // Import styles
import {
  BTabs,
  BTab,
  BSpinner,
  BCardText,
  BCard,
  BButton,
  BTable,
  BRow,
  BCol,
  BFormCheckbox,
  BDropdown,
  BDropdownItem,
  BPagination,
  BInputGroup,
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BFormSelect,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import { formatDate } from "@/libs/customFn";

export default {
  components: {
    BTabs,
    BCardText,
    BCard,
    BTab,
    BButton,
    BSpinner,
    BTable,
    BRow,
    BCol,
    Calendar,
    BFormCheckbox,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BFormSelect,
    DatePicker,
  },
  data() {
    return {
      perPage: 10,
      pageOptions: [3, 5, 10],
      currentPage: 1,
      totalRows: 1,
      sortBy: "date",
      sortDesc: true,
      filter: null,
      filters: {
        title: "",
        date: null,
        status: null,
        assignee: "",
        customer: "",
      },
      filterOn: [],
      displayCalendar: false,
      loadingSpinner: true,
      statusFilter: [],
      CalendarDetails: {
        viewSidebar: false,
        legend: null,
        events: [],
      },
      calendarFilters: {
        customer: "",
        assignee: "",
      },
      calendarKey: 0,
      fields: [
        { key: "title", label: "Template Name", sortable: true },
        { key: "Assignee", label: "Assignee", sortable: true },
        { key: "Customer", label: "Customer", sortable: true },

        {
          key: "date",
          label: "Appointment Date",
          sortable: true,
          formatter: (value, key, item) => formatDate(item.date),
        },
        { key: "status", label: "Status", sortable: true },
        { key: "actions", label: "Actions" },
      ],

      items: [], // Dynamically populated,
      filteredItems: [],
      statusOptions: [
        { value: "BOOKING_PENDING", text: "Booking Pending" },
        { value: "BOOKING_CANCELLED", text: "Booking Cancelled" },
        { value: "BOOKING_CONFIRMED", text: "Booking Confirmed" },
        { value: "SR_IN_PROGRESS", text: "Service In Progress" },
        { value: "SR_APPROVED", text: "Service Approved" },
        { value: "SR_COMPLETED", text: "Service Completed" },
        { value: "SR_INVOICED", text: "Service Invoiced" },
      ],
      serviceOrderStatus: [
        {
          status: "BOOKING_PENDING",
          variant: "info",
          backgroundColor: "#FFA500",
          textColor: "#FFF",
          borderColor: "#FFA500",
          color: "#FFA500",
        },
        {
          status: "BOOKING_CANCELLED",
          variant: "danger",
          backgroundColor: "#9FC54C",
          textColor: "#FFF",
          borderColor: "#9FC54C",
          color: "#9FC54C",
        },
        {
          status: "BOOKING_CONFIRMED",
          variant: "success",
          backgroundColor: "#0000FF",
          textColor: "#FFF",
          borderColor: "#0000FF",
          color: "#0000FF",
        },
        {
          status: "SR_IN_PROGRESS",
          variant: "secondary",
          backgroundColor: "#ffd500",
          textColor: "#FFF",
          borderColor: "#ffd500",
          color: "#ffd500",
        },
        {
          status: "SR_APPROVED",
          variant: "info",
          backgroundColor: "#4A7CBD",
          textColor: "#FFF",
          borderColor: "#4A7CBD",
          color: "#4A7CBD",
        },
        {
          status: "SR_COMPLETED",
          variant: "success",
          backgroundColor: "#008000",
          textColor: "#FFF",
          borderColor: "#008000",
          color: "#008000",
        },
        {
          status: "SR_INVOICED",
          variant: "success",
          backgroundColor: "#00ff00c9",
          textColor: "#FFF",
          borderColor: "#00ff00c9",
          color: "#00ff00c9",
        },
      ],
    };
  },
  mounted() {
  },
  methods: {
    applyFilters() {
      if (
        !this.filters.title &&
        !this.filters.date &&
        !this.filters.status &&
        !this.filters.assignee && // Add this condition
        !this.filters.customer // Add this condition
      ) {
        // No filters are applied; reset to original items
        this.filteredItems = [...this.items];
      } else {
        // Apply the filters
        this.filteredItems = this.items.filter((item) => {
          const matchesTitle = item.title
            .toLowerCase()
            .includes(this.filters.title.toLowerCase());

     

          // const matchesDate =
          //   !this.filters.date ||
          //   (Array.isArray(this.filters.date)
          //     ? new Date(item.date) >= new Date(this.filters.date[0]) &&
          //     new Date(item.date) <= new Date(this.filters.date[1])
          //     : formatDate(item.date) === this.filters.date);

          const matchesDate =
            !this.filters.date ||
            (Array.isArray(this.filters.date)
              ? (() => {
                  const start = new Date(this.filters.date[0]);
                  const end = new Date(this.filters.date[1]);

                  // Set end date to end of day (23:59:59.999)
                  end.setHours(23, 59, 59, 999);

                  const itemDate = new Date(item.date);
                  return itemDate >= start && itemDate <= end;
                })()
              : formatDate(item.date) === this.filters.date);

          const matchesStatus =
            !this.filters.status || item.status === this.filters.status;

          const matchesAssignee = this.filters.assignee
            ? item.assignee &&
              item.assignee
                .toLowerCase()
                .includes(this.filters.assignee.toLowerCase())
            : true;

          const matchesCustomer = this.filters.customer
            ? item.name &&
              item.name
                .toLowerCase()
                .includes(this.filters.customer.toLowerCase())
            : true;

          return (
            matchesTitle &&
            matchesDate &&
            matchesStatus &&
            matchesAssignee &&
            matchesCustomer
          );
        });
      }

      // Reset pagination to the first page
      this.totalRows = this.filteredItems.length;
      this.currentPage = 1;
    },
    preventClear(event) {
      event.preventDefault();
    },
    openCalendar(event) {
      // Ensure the calendar opens on focus
      event.target.blur(); // Remove focus to prevent further typing
      event.target.click(); // Programmatically open the calendar

      // Add an event listener to detect clicks outside the date picker
      document.addEventListener("click", this.closeCalendar);
    },
    closeCalendar(event) {
      const datePicker = this.$refs.datePicker.$el;

      // Check if the click happened outside the date picker
      if (!datePicker.contains(event.target)) {
        // Close the calendar by clicking outside
        this.$refs.datePicker.hideCalendar();

        // Remove the event listener after closing
        document.removeEventListener("click", this.closeCalendar);
      }
    },

    clearFilters() {
      // Clear all filters and reset the table
      this.filters = {
        title: "",
        date: null,
        status: null,
        assignee: "",
        customer: "",
      };
      this.applyFilters();
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },//toggle sort asc or desc 
    toggleSort(field) {
      if (this.sortBy === field) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = field;
        this.sortDesc = false;
      }
    },
    ...mapActions("serviceModule", ["getServiceOrderListAction"]),
    editServiceOrder(item) {
      // Logic to navigate to the edit page
      this.$router.push(`/serviceorder/edit/${item.referenceNo}`);
    },
    addServiceOrderTimesheet(item) {
      this.$router.push(`/servicetimesheet/add/${item.id}`);
    },
    getServiceOrderStatus(status) {
      const statuses = {
        BOOKING_PENDING: { backgroundColor: "#FFA500", borderColor: "#FFA500" },
        BOOKING_CANCELLED: {
          backgroundColor: "#9FC54C",
          borderColor: "#9FC54C",
        },
        BOOKING_CONFIRMED: {
          backgroundColor: "#0000FF",
          borderColor: "#0000FF",
        },
        SR_IN_PROGRESS: { backgroundColor: "#ffd500", borderColor: "#ffd500" },
        SR_COMPLETED: { backgroundColor: "#008000", borderColor: "#008000" },
        SR_APPROVED: { backgroundColor: "#4A7CBD", borderColor: "#4A7CBD" },
        SR_INVOICED: { backgroundColor: "#00ff00c9", borderColor: "#00ff00c9" },
      };
      return (
        statuses[status] || { backgroundColor: "#FFF", borderColor: "#000" }
      );
    },
    applyCalendarFilters() {
      this.loadCalendarEvents(); // Correct usage
    },
    loadCalendarEvents() {
      console.log(
        "loadCalendarEvents called with filters:",
        this.calendarFilters
      );

      const statusColorMap = {
        BOOKING_PENDING: "#545b62",
        BOOKING_CANCELLED: "#dc3545",
        BOOKING_CONFIRMED: "#1e7e34",
        SR_IN_PROGRESS: "#6c757d",
        SR_COMPLETED: "#1e7e34",
        SR_APPROVED: "#1e7e34",
      };

      let filteredOrders = this.serviceOrderList;

      if (this.calendarFilters.customer) {
        filteredOrders = filteredOrders.filter((order) =>
          (order.firstName + " " + order.lastName)
            .toLowerCase()
            .includes(this.calendarFilters.customer.toLowerCase())
        );
      }

      if (this.calendarFilters.assignee) {
        filteredOrders = filteredOrders.filter((order) =>
          order.assignedTechnicianDetail[0]?.assignedStaffName
            ?.toLowerCase()
            .includes(this.calendarFilters.assignee.toLowerCase())
        );
      }

      console.log("Filtered Orders Length:", filteredOrders.length);

      const events = filteredOrders.map((order) => {
        const color = statusColorMap[order.status] || "#000000"; // Fallback to black if status is missing
        console.log("Order Status:", order.status, "Assigned Color:", color);
        return {
          title: order.orderTemplateName,
          start: order.appointmentDateTime,
          name: order.firstName + " " + order.lastName,
          assignee: order.assignedTechnicianDetail[0]?.assignedStaffName,
          status: order.status,
          color: "#ff0000",
          id: order.referenceNo,
          link: `/serviceorder/edit/${order.referenceNo}`,
        };
      });

      this.CalendarDetails.events = events;
      console.log("Updated Calendar Events:", this.CalendarDetails.events);
      this.calendarKey += 1; // Trigger a re-render if needed
    },
    resetCalendarFilters() {
      this.calendarFilters = { customer: "", assignee: "" }; // Reset the filters
      this.applyCalendarFilters(); // Re-apply the filters with reset values
    },
  },
  // Watch displayCalendar to load events when toggled
  toggleCalendar() {
    if (this.displayCalendar) {
      this.loadCalendarEvents();
    }
  },
  handleEventClick(event) {
    console.log("Event clicked:", event);
    if (event && event.id) {
      // Navigate to the service order edit page
      this.$router.push(`/serviceorder/edit/${event.id}`);
    } else {
      console.error("Event ID not found!");
    }
  },
  watch: {
    displayCalendar(newVal) {
      if (newVal) this.toggleCalendar();
    },
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter((f) => f.sortable)
        .map((f) => ({ text: f.label, value: f.key }));
    },
    ...mapGetters("serviceModule", {
      serviceOrderList: "serviceOrderList",
      loading: "loading",
    }),
  },
  async mounted() {
    try {
      // Fetch Service Order List
      await this.getServiceOrderListAction().then(() => {
        // Populate Calendar events
        // this.CalendarDetails.events = this.serviceOrderList.map((sm) => ({
        //   id: sm.referenceNo,
        //   title: sm.orderTemplateName,
        //   start: new Date(sm.appointmentDateTime),
        //   name: sm.firstName + " " + sm.lastName,
        //   assignee: sm.assignedTechnicianDetail[0]?.assignedStaffName,
        //   status: sm.status,
        //   textColor: "#fff",
        //   backgroundColor: this.getServiceOrderStatus(sm.status)
        //     .backgroundColor,
        //   borderColor: this.getServiceOrderStatus(sm.status).borderColor,
        //   link: `/serviceorder/edit/${sm.referenceNo}`,
        // }));

        this.CalendarDetails.events = this.serviceOrderList.map((sm) => {
          let link = ""; // Default link
          if (sm.status === "SR_COMPLETED") {
            link = `/serviceorder/view/${sm.referenceNo}`;
          } else if (sm.status === "SR_INVOICED") {
            link = `/invoice/view/${sm.salesReference}`;
          } else {
            link = `/serviceorder/edit/${sm.referenceNo}`;
          }

          return {
            id: sm.referenceNo,
            title: sm.orderTemplateName,
            start: new Date(sm.appointmentDateTime),
            name: sm.firstName + " " + sm.lastName,
            assignee: sm.assignedTechnicianDetail[0]?.assignedStaffName,
            status: sm.status,
            textColor: "#fff",
            backgroundColor: this.getServiceOrderStatus(sm.status)
              .backgroundColor,
            borderColor: this.getServiceOrderStatus(sm.status).borderColor,
            link: link, // Assign the dynamically determined link here
          };
        });

        // Populate table items with name and date
        this.items = this.serviceOrderList.map((sm) => ({
          title: sm.orderTemplateName,
          date: new Date(sm.appointmentDateTime),
          name: sm.firstName + " " + sm.lastName,
          assignee: sm.assignedTechnicianDetail[0]?.assignedStaffName,
          status: sm.status,
          referenceNo: sm.referenceNo,
          id: sm.id,
          salesReference: sm.salesReference,
        }));

        this.filteredItems = [...this.items]; // Initialize filtered items
        this.totalRows = this.filteredItems.length; // Set total rows
      });
    } catch (error) {
      console.error("Error fetching service orders:", error);
    } finally {
      this.loadingSpinner = false;
    }
  },
};
</script>

<style scoped>
.color-legend h5 {
  font-weight: bold;
  margin-bottom: 10px;
}

.color-legend .d-flex {
  display: flex;
  align-items: center;
}

.color-legend .d-flex div {
  border-radius: 3px;
}

.custom-dropdown-item {
  padding: 10px 20px;
  /* Adjusted padding for top, bottom, left, and right */
  display: flex;
  align-items: center;
}

.custom-checkbox {
  margin-left: 10px;
  /* Add spacing between checkbox and text */
  width: 100%;
  /* Ensure the checkbox and text take full width */
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Push text and checkbox apart */
}

.custom-checkbox input {
  margin-right: 10px;
  /* Add space between the checkbox and text */
}

.status-badge {
  display: inline-block;
  width: 200px;
  /* Fixed width for all status badges */
  text-align: center;
  /* Align text to the center */
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  white-space: nowrap;
  /* Prevent text from wrapping */
}

.column-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.mt-1 {
  margin-top: 0.25rem;
}

.vue2-datepicker .date-picker__clear-wrapper {
  display: none !important;
}

.vue2-datepicker input {
  cursor: default;
  /* Prevent click pointer on input field */
}

.no-scrollbar {
  scrollbar-width: none;
  /* For Firefox */
  -ms-overflow-style: none;
  /* For Internet Explorer and Edge */
}
</style>
